import React from 'react'
import { Separator } from '@components/ui/separator'
import { cn } from '@/utils'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type PhotoActionHandlers,
  PhotoActionsList,
} from '@/components/app/photos/photo-actions-list'
import { usePhotoPanelContext } from '@/hooks/use-photo-panel-context'
import { usePhotoPlacedLabel } from '@/hooks/use-photo-placed-label'
import type { PhotoActionId } from '@/lib/photos/photo-action-items'

interface PhotosPanelProps {
  className?: string
  interactionLocked?: boolean
  selectedPhotoShape?: ShapeData
  onAddCaption?: () => void
  onCropPhoto?: () => void
  onRemovePhoto?: () => void
  onRemoveBackground?: () => void
  onClippingPath?: () => void
  /** Opens the Photo Effects popover (WMPCM-9470). */
  onPhotoEffects?: () => void
  /** True while the Photo Effects popover is open — drives trigger active/ARIA state. */
  photoEffectsOpen?: boolean
  isRemovingBackground?: boolean
}

interface InfoRowProps {
  label: string
  value: string
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="flex gap-1 items-start w-full">
    <div className="w-[100px] shrink-0 text-sm font-normal text-muted-foreground">{label}</div>
    <div className="flex-1 min-w-0 text-sm font-normal text-foreground text-right">{value}</div>
  </div>
)

/** Figma Metadata separator — ~2px dots with ~4px gaps (CSS 1px border-dotted is too fine). */
const MetadataDottedSeparator: React.FC = () => (
  <div
    className="h-[2px] w-full"
    role="separator"
    aria-orientation="horizontal"
    style={{
      backgroundImage: 'radial-gradient(circle, var(--border) 1.25px, transparent 1.35px)',
      backgroundPosition: 'left center',
      backgroundRepeat: 'repeat-x',
      backgroundSize: '6px 2px',
    }}
  />
)

export const PhotosPanel: React.FC<PhotosPanelProps> = ({
  className,
  interactionLocked = false,
  selectedPhotoShape,
  onAddCaption,
  onCropPhoto,
  onRemovePhoto,
  onRemoveBackground,
  onClippingPath,
  onPhotoEffects,
  photoEffectsOpen = false,
  isRemovingBackground = false,
}) => {
  const {
    fileNameLabel,
    hasPortraitMetadata,
    portraitStudentName,
    taggedLines,
    dimensionsLabel,
    fileSize,
    albumLabel,
    uploadedByLabel,
    approvedByLabel,
    portraitLabel,
    isActionDisabled,
  } = usePhotoPanelContext(selectedPhotoShape, {
    interactionLocked,
    isRemovingBackground,
  })

  const placedLabel = usePhotoPlacedLabel(selectedPhotoShape)

  const actionHandlers = React.useMemo<PhotoActionHandlers>(
    () => ({
      photoEffects: onPhotoEffects,
      cropPhoto: onCropPhoto,
      clippingPath: onClippingPath,
      removeBackground: onRemoveBackground,
      caption: onAddCaption,
      removePhoto: onRemovePhoto,
    }),
    [onAddCaption, onCropPhoto, onClippingPath, onPhotoEffects, onRemoveBackground, onRemovePhoto]
  )

  const isActionActive = React.useCallback(
    (id: PhotoActionId) => id === 'photoEffects' && photoEffectsOpen,
    [photoEffectsOpen]
  )

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        className,
        interactionLocked && 'pointer-events-none select-none opacity-60'
      )}
      aria-disabled={interactionLocked || undefined}
    >
      <p className="text-sm font-medium text-foreground leading-5">General Info</p>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {!hasPortraitMetadata && <InfoRow label="File name" value={fileNameLabel} />}
          <InfoRow label="File size" value={fileSize} />
          <InfoRow label="Dimensions" value={dimensionsLabel} />
        </div>

        <MetadataDottedSeparator />

        {hasPortraitMetadata ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-foreground">Portrait</p>
            <InfoRow label="Name" value={portraitStudentName} />
            <InfoRow label="Grade" value="—" />
            <InfoRow label="Placed" value={placedLabel} />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <InfoRow label="Album" value={albumLabel} />
            <InfoRow label="Uploaded by" value={uploadedByLabel} />
            <InfoRow label="Approved by" value={approvedByLabel} />
            <div className="flex gap-1 items-start w-full">
              <div className="w-[100px] shrink-0 text-sm font-normal text-muted-foreground">
                Tagged
              </div>
              <div className="flex-1 min-w-0 text-right">
                {taggedLines.length > 0 ? (
                  taggedLines.map((line, index) => (
                    <p key={index} className="text-sm font-normal text-foreground">
                      {line}
                    </p>
                  ))
                ) : (
                  <p className="text-sm font-normal text-foreground">None</p>
                )}
              </div>
            </div>
            <InfoRow label="Portrait" value={portraitLabel} />
            <InfoRow label="Placed" value={placedLabel} />
          </div>
        )}
      </div>

      <Separator />

      <p className="text-sm font-medium text-foreground leading-5">Photo Actions</p>

      <PhotoActionsList
        handlers={actionHandlers}
        isDisabled={isActionDisabled}
        isActionActive={isActionActive}
        isRemovingBackground={isRemovingBackground}
      />
    </div>
  )
}
